import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import DemoPlayer from "@/components/feature/DemoPlayer";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import MatchCard from "@/components/base/MatchCard";
import { matches } from "@/mocks/matches";
import { analyses } from "@/mocks/analyses";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

const eventMeta: Record<string, { icon: string; label: string; color: string }> = {
  goal: { icon: "ri-football-fill", label: "进球", color: "text-primary-600" },
  yellow: { icon: "ri-checkbox-blank-circle-fill", label: "黄牌", color: "text-accent-500" },
  red: { icon: "ri-checkbox-blank-circle-fill", label: "红牌", color: "text-red-500" },
  sub: { icon: "ri-swap-line", label: "换人", color: "text-secondary-500" },
};

function StatBar({ label, home, away }: { label: string; home: number; away: number }) {
  const total = home + away || 1;
  const homePct = Math.round((home / total) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-foreground-500 mb-1.5">
        <span className="font-label text-sm text-foreground-900 font-medium">{home}</span>
        <span>{label}</span>
        <span className="font-label text-sm text-foreground-900 font-medium">{away}</span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-secondary-100">
        <div className="bg-primary-500" style={{ width: `${homePct}%` }} />
        <div className="bg-accent-500" style={{ width: `${100 - homePct}%` }} />
      </div>
    </div>
  );
}

export default function Detail() {
  const { matchId } = useParams<{ matchId: string }>();
  const match = matches.find((m) => m.id === matchId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta(
    match
      ? `${match.home.name} vs ${match.away.name} 比赛详情及直播 - 51网站在线观看免费播播足球`
      : "赛事详情 - 51网站在线观看免费播播足球",
    match
      ? `${match.leagueName}${match.round}，${match.home.name}对阵${match.away.name}，比赛时间 ${match.date} ${match.time}，查看实时赛况、数据统计与精彩回放。`
      : "查看足球赛事详情、实时赛况与数据统计。",
  );

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!match) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-4xl" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-foreground-950">
            没有找到这场比赛
          </h1>
          <p className="mt-3 text-sm text-foreground-500">
            比赛可能已下架或链接有误，看看其他精彩赛事吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const fav = isFavorite(match.id);
  const related = matches
    .filter((m) => m.leagueKey === match.leagueKey && m.id !== match.id)
    .slice(0, 4);
  const relatedAnalyses = analyses
    .filter((a) => a.tags.includes(match.leagueName))
    .slice(0, 2);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <SiteLayout>
      <PageHeader
        title={`${match.home.name} vs ${match.away.name}`}
        subtitle={`${match.leagueName} · ${match.round} · ${match.date} ${match.time} · ${match.venue}`}
        crumbs={[
          { label: "首页", to: "/" },
          { label: "赛事分类", to: `/category/${match.leagueKey}` },
          { label: match.leagueName, to: `/category/${match.leagueKey}` },
          { label: `${match.home.name} vs ${match.away.name}` },
        ]}
      />

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
              <div className="flex items-center justify-center mb-6">
                <StatusBadge status={match.status} minute={match.minute} />
              </div>
              <div className="flex items-center justify-between gap-3 md:gap-6">
                <div className="flex flex-col items-center gap-3 flex-1 min-w-0">
                  <TeamBadge short={match.home.short} color={match.home.color} size="lg" />
                  <span className="text-sm md:text-base font-semibold text-foreground-900 text-center">
                    {match.home.name}
                  </span>
                  <span className="text-xs text-foreground-400">主队</span>
                </div>

                <div className="shrink-0 text-center px-2">
                  {match.status === "upcoming" ? (
                    <>
                      <span className="font-label text-3xl md:text-4xl font-semibold text-foreground-950">
                        {match.time}
                      </span>
                      <p className="text-xs text-foreground-400 mt-2">{match.date}</p>
                    </>
                  ) : (
                    <>
                      <span className="font-label text-5xl md:text-6xl font-semibold text-foreground-950 tracking-wider">
                        {match.score?.home}
                        <span className="text-foreground-300 mx-2">:</span>
                        {match.score?.away}
                      </span>
                      <p className="text-xs text-foreground-400 mt-2">
                        {match.status === "live" ? `进行中 ${match.minute}'` : "全场结束"}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex flex-col items-center gap-3 flex-1 min-w-0">
                  <TeamBadge short={match.away.short} color={match.away.color} size="lg" />
                  <span className="text-sm md:text-base font-semibold text-foreground-900 text-center">
                    {match.away.name}
                  </span>
                  <span className="text-xs text-foreground-400">客队</span>
                </div>
              </div>
            </div>

            {match.status === "upcoming" ? (
              <div className="bg-background-100 border border-background-200 rounded-lg py-16 text-center">
                <i className="ri-timer-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-500">
                  比赛尚未开始，{match.date} {match.time} 开赛
                </p>
                <p className="mt-1 text-xs text-foreground-400">
                  开赛后这里将显示直播/回放区域
                </p>
              </div>
            ) : (
              <div>
                <DemoPlayer
                  title={`${match.home.name} vs ${match.away.name}`}
                  cover={match.cover}
                  isLive={match.status === "live"}
                />
                <p className="mt-3 text-xs text-foreground-400 flex items-start gap-2">
                  <i className="ri-information-line mt-0.5" />
                  {match.status === "live"
                    ? "当前为前端演示播放区域，播放与暂停按钮仅作示意，暂未接入真实直播流媒体接口。"
                    : "本场回放为前端演示播放区域，点击播放/暂停可查看演示效果，暂未接入真实视频接口。"}
                </p>
              </div>
            )}

            {match.events.length > 0 && (
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
                <h2 className="text-base md:text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-flashlight-line text-primary-500" />
                  比赛事件
                </h2>
                <ol className="relative border-l border-background-200 ml-2 flex flex-col gap-5">
                  {match.events.map((ev, i) => {
                    const meta = eventMeta[ev.type];
                    const team = ev.side === "home" ? match.home : match.away;
                    return (
                      <li key={i} className="pl-6 relative">
                        <span
                          className={`absolute -left-[9px] top-0.5 w-4 h-4 rounded-full bg-background-50 border-2 border-background-300 flex items-center justify-center ${meta.color}`}
                        >
                          <i className={`${meta.icon} text-[8px]`} />
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="font-label text-sm font-semibold text-foreground-950 w-10">
                            {ev.minute}'
                          </span>
                          <TeamBadge short={team.short} color={team.color} size="sm" />
                          <span className="text-sm text-foreground-800">{ev.player}</span>
                          <span className={`text-xs ${meta.color}`}>{meta.label}</span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {match.stats.length > 0 && (
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
                <h2 className="text-base md:text-lg font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-primary-500" />
                  数据统计
                </h2>
                <div className="flex items-center justify-between text-xs text-foreground-500 mb-5">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-primary-500" />
                    {match.home.name}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-accent-500" />
                    {match.away.name}
                  </span>
                </div>
                <div className="flex flex-col gap-4">
                  {match.stats.map((s) => (
                    <StatBar key={s.label} label={s.label} home={s.home} away={s.away} />
                  ))}
                </div>
              </div>
            )}

            {relatedAnalyses.length > 0 && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-5 md:p-6">
                <h2 className="text-base md:text-lg font-semibold text-primary-900 mb-4 flex items-center gap-2">
                  <i className="ri-article-line" />
                  相关赛事解析
                </h2>
                <div className="flex flex-col gap-3">
                  {relatedAnalyses.map((a) => (
                    <Link
                      key={a.id}
                      to={`/analysis?article=${a.id}`}
                      className="flex items-center justify-between gap-3 bg-background-50 rounded-md p-3.5 hover:bg-white transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-800 line-clamp-1">
                        {a.title}
                      </span>
                      <i className="ri-arrow-right-line text-primary-600 shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-6">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => {
                    toggleFavorite(match.id);
                    setToast(fav ? "已取消收藏" : "已加入我的收藏");
                  }}
                  className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    fav
                      ? "bg-accent-500 text-white hover:bg-accent-600"
                      : "bg-background-100 text-foreground-800 hover:bg-background-200"
                  }`}
                >
                  <i className={fav ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                  {fav ? "已收藏本场比赛" : "收藏本场比赛"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  分享这场比赛
                </button>
              </div>
              <div className="mt-5 pt-4 border-t border-background-100 flex flex-col gap-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-400">联赛</span>
                  <Link
                    to={`/category/${match.leagueKey}`}
                    className="text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {match.leagueName}
                  </Link>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-400">轮次</span>
                  <span className="text-foreground-800">{match.round}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-400">时间</span>
                  <span className="text-foreground-800">
                    {match.date} {match.time}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-foreground-400 shrink-0">球场</span>
                  <span className="text-foreground-800 text-right">{match.venue}</span>
                </div>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h2 className="text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-primary-500" />
                相关推荐
              </h2>
              {related.length === 0 ? (
                <p className="text-sm text-foreground-400">暂无同联赛其他赛事</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {related.map((m) => (
                    <div key={m.id}>
                      <MatchCard match={m} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-foreground-950 text-white text-sm animate-fade-in">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}